import { useMemo } from 'react';

import isNil from 'lodash/isNil';

import { TileRate, TileRateProvision } from '@nocowanie/noc-api';

import { dateHelpers, formatHelpers } from '@nocowanie/core';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';

export const useAccommodationRatePlanFeatures = (
    t: AccommodationTileProps['t'],
    rate?: TileRate,
) => {
    const features = useMemo(() => {
        if (isNil(rate)) {
            return null;
        }

        const { cancellationPolicy, prepayment, provision } = rate;
        const isCancellationFree = cancellationPolicy?.freeCancellation;
        const isCancellationNonRefundable = cancellationPolicy?.notRefundable;
        const features = [];

        if (provision && provision.type !== TileRateProvision.type.NO_MEAL) {
            features.push({
                key: 'provision',
                dataTestId: 'acc-tile-provision',
                color: 'green.950',
                content: t('ratePlan.provisionIncluded', {
                    provision: provision.name,
                }),
                showOnMobile: true,
            });
        }

        if (isCancellationFree || isCancellationNonRefundable) {
            const cancellationText = () => {
                if (isCancellationFree) {
                    if (cancellationPolicy.cancellationDateTo) {
                        return t('ratePlan.cancellationPolicies.freeUntil', {
                            date: dateHelpers
                                .format(new Date(cancellationPolicy.cancellationDateTo), 'd MMM')
                                .toLowerCase(),
                        });
                    }

                    return t('ratePlan.cancellationPolicies.free');
                } else if (isCancellationNonRefundable) {
                    return t('ratePlan.cancellationPolicies.nonRefundable');
                }

                return null;
            };

            const text = cancellationText();

            if (text) {
                features.push({
                    key: 'cancellation',
                    dataTestId: 'acc-tile-cancellation',
                    color: isCancellationFree ? 'green.950' : undefined,
                    showOnMobile: isCancellationFree,
                    content: text,
                });
            }
        }

        if (prepayment) {
            const noPrepayment = prepayment.rate === 0;
            const prepaymentText = () => {
                if (noPrepayment) {
                    return t('ratePlan.prepayment.noPrepayment');
                }
                if (prepayment.fullPrepayment) {
                    return t('ratePlan.prepayment.fullPrepayment');
                }
                if (typeof prepayment.convertedRate === 'number') {
                    return t('ratePlan.prepayment.partialPrepayment', {
                        prepayment: formatHelpers.price(
                            prepayment.convertedRate,
                            rate.convertedCurrency,
                            false,
                            true,
                        ),
                    });
                }
            };

            features.push({
                key: 'prepayment',
                dataTestId: 'acc-tile-prepayment',
                content: prepaymentText(),
                showOnMobile: false,
                color: noPrepayment ? 'green.950' : undefined,
            });
        }

        return features;
    }, [rate, t]);

    return features;
};
